import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

import { resourceService } from '../../../services/resourceService';

import { supabase } from '../../../lib/supabase';

// ─── Edit Assignment Modal ────────────────────────────────────────────────────
const EditAssignmentModal = ({ assignment, resource, onClose, onSave, allAssignments }) => {
  const [formData, setFormData] = useState({
    startDate: assignment?.startDate || '',
    endDate: assignment?.endDate || '',
    projectName: assignment?.projectName || assignment?.phaseName || ''
  });
  const [conflictWarning, setConflictWarning] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const checkForNewConflicts = useCallback((startDate, endDate) => {
    if (!startDate || !endDate) return null;
    const start = new Date(startDate);
    const end = new Date(endDate);
    // Check other assignments for this resource that overlap with the new dates
    const overlapping = allAssignments?.filter(a => {
      if (a?.id === assignment?.id) return false;
      if (a?.resourceId !== resource?.id && a?.resource_id !== resource?.id) return false;
      const aStart = new Date(a?.startDate || a?.start_date);
      const aEnd = new Date(a?.endDate || a?.end_date);
      return start <= aEnd && end >= aStart;
    });
    return overlapping?.length > 0 ? overlapping : null;
  }, [allAssignments, assignment?.id, resource?.id]);

  const handleChange = (e) => {
    const { name, value } = e?.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);
    if (updated?.startDate && updated?.endDate) {
      const conflicts = checkForNewConflicts(updated?.startDate, updated?.endDate);
      setConflictWarning(conflicts);
    }
  };

  const handleSave = async () => {
    if (!formData?.startDate || !formData?.endDate) {
      setError('Start and end dates are required.');
      return;
    }
    if (new Date(formData?.startDate) > new Date(formData?.endDate)) {
      setError('End date must be after start date.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onSave(assignment, { startDate: formData?.startDate, endDate: formData?.endDate });
      onClose();
    } catch (err) {
      setError(err?.message || 'Failed to save assignment.');
    } finally {
      setSaving(false);
    }
  };

  const fmt = (d) => d ? new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="text-lg font-semibold text-foreground">Edit Assignment</h2>
          <button onClick={onClose} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <Icon name="X" size={18} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* Resource info */}
          <div className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
              {resource?.imageUrl ? (
                <Image src={resource?.imageUrl} alt={resource?.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground text-sm font-medium">
                  {resource?.name?.charAt(0)}
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-foreground">{resource?.name}</p>
              <p className="text-xs text-muted-foreground capitalize">{resource?.category}</p>
            </div>
          </div>

          {/* Assignment label */}
          <div>
            <p className="text-xs text-muted-foreground mb-1">Assignment</p>
            <p className="text-sm font-medium text-foreground">{formData?.projectName || 'Assignment'}</p>
          </div>

          {/* Date fields */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Start Date</label>
              <input
                type="date"
                name="startDate"
                value={formData?.startDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">End Date</label>
              <input
                type="date"
                name="endDate"
                value={formData?.endDate}
                onChange={handleChange}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Conflict warning */}
          {conflictWarning && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-semibold text-amber-800 mb-1">New conflict detected with these dates</p>
                  {conflictWarning?.map((a, i) => (
                    <p key={i} className="text-xs text-amber-700">
                      • {a?.projectName || a?.phaseName || 'Assignment'} ({fmt(a?.startDate || a?.start_date)} – {fmt(a?.endDate || a?.end_date)})
                    </p>
                  ))}
                  <p className="text-xs text-amber-700 mt-1">You can still save, but this will create a new conflict.</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            variant={conflictWarning ? 'destructive' : 'default'}
            onClick={handleSave}
            disabled={saving}
            iconName={saving ? 'Loader2' : 'Save'}
            iconPosition="left"
          >
            {saving ? 'Saving...' : conflictWarning ? 'Save Anyway' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

// ─── Main ResourceConflictTab ─────────────────────────────────────────────────
const ResourceConflictTab = () => {
  const [allAssignments, setAllAssignments] = useState([]);
  const [conflicts, setConflicts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [periodPreset, setPeriodPreset] = useState('30');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  // Edit modal
  const [editingAssignment, setEditingAssignment] = useState(null);
  const [editingResource, setEditingResource] = useState(null);

  const categories = [
    { id: 'all', name: 'All Categories' },
    { id: 'employees', name: 'Employees' },
    { id: 'contractors', name: 'Contractors' },
    { id: 'teams', name: 'Teams' },
    { id: 'trucks', name: 'Trucks' },
    { id: 'trailers', name: 'Trailers' },
    { id: 'equipment', name: 'Equipment' },
    { id: 'tools', name: 'Tools' },
    { id: 'other', name: 'Other' }
  ];

  const getDateRange = useCallback(() => {
    const today = new Date();
    today?.setHours(0, 0, 0, 0);
    if (periodPreset === 'custom') {
      return {
        start: customStart ? new Date(customStart) : null,
        end: customEnd ? new Date(customEnd) : null
      };
    }
    const days = parseInt(periodPreset, 10);
    const end = new Date(today);
    end?.setDate(end?.getDate() + days);
    return { start: today, end };
  }, [periodPreset, customStart, customEnd]);

  const loadAndDetectConflicts = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all resource assignments with resource info
      const { data: assignmentsData, error: assignErr } = await supabase
        ?.from('resource_assignments')
        ?.select(`
          *,
          resource:resources(id, name, category, status)
        `)
        ?.order('start_date', { ascending: true });

      if (assignErr) throw assignErr;

      // Also fetch deliverable-based assignments
      const { data: deliverableAssignments, error: delErr } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.select(`
          id,
          resource_id,
          start_date,
          end_date,
          deliverable:deliverables(id, name, start_date, end_date, project_id)
        `);

      // Fetch resources for deliverable assignments
      const { data: resourcesData } = await supabase
        ?.from('resources')
        ?.select('id, name, category, status');

      const resourceMap = {};
      (resourcesData || [])?.forEach(r => { resourceMap[r?.id] = r; });

      // Fetch project names
      const projectIds = [...new Set(
        (deliverableAssignments || [])?.map(da => da?.deliverable?.project_id)?.filter(Boolean)
      )];
      let projectNameMap = {};
      if (projectIds?.length > 0) {
        const { data: projects } = await supabase
          ?.from('projects')
          ?.select('id, project_name, project_number')
          ?.in('id', projectIds);
        (projects || [])?.forEach(p => {
          projectNameMap[p?.id] = p?.project_name || p?.project_number || 'Project';
        });
      }

      // Normalize direct assignments
      const directNormalized = (assignmentsData || [])?.map(a => ({
        id: a?.id,
        resourceId: a?.resource_id,
        resource: a?.resource,
        projectName: a?.project_name || a?.phase_name || 'Assignment',
        phaseName: a?.phase_name,
        startDate: a?.start_date,
        endDate: a?.end_date,
        source: 'direct',
        rawId: a?.id
      }));

      // Normalize deliverable assignments
      const deliverableNormalized = (deliverableAssignments || [])
        ?.filter(da => da?.start_date && da?.end_date)
        ?.map(da => ({
          id: `del-${da?.id}`,
          resourceId: da?.resource_id,
          resource: resourceMap?.[da?.resource_id] || null,
          projectName: da?.deliverable?.project_id
            ? (projectNameMap?.[da?.deliverable?.project_id] || 'Project')
            : (da?.deliverable?.name || 'Deliverable'),
          phaseName: da?.deliverable?.name,
          startDate: da?.start_date,
          endDate: da?.end_date,
          source: 'deliverable',
          rawId: da?.id,
          deliverableId: da?.deliverable?.id
        }));

      const combined = [...directNormalized, ...deliverableNormalized];
      setAllAssignments(combined);

      // Detect conflicts: group by resource, find overlapping pairs
      const byResource = {};
      combined?.forEach(a => {
        if (!a?.resourceId) return;
        if (!byResource?.[a?.resourceId]) byResource[a?.resourceId] = [];
        byResource?.[a?.resourceId]?.push(a);
      });

      const detectedConflicts = [];
      Object.entries(byResource)?.forEach(([resourceId, assignments]) => {
        if (assignments?.length < 2) return;
        const sorted = [...assignments]?.sort((a, b) => new Date(a?.startDate) - new Date(b?.startDate));
        const conflictPairs = [];
        for (let i = 0; i < sorted?.length; i++) {
          for (let j = i + 1; j < sorted?.length; j++) {
            const a = sorted?.[i];
            const b = sorted?.[j];
            const aStart = new Date(a?.startDate);
            const aEnd = new Date(a?.endDate);
            const bStart = new Date(b?.startDate);
            const bEnd = new Date(b?.endDate);
            if (aStart <= bEnd && aEnd >= bStart) {
              // They overlap
              const alreadyIn = conflictPairs?.some(p =>
                (p?.a?.id === a?.id && p?.b?.id === b?.id) ||
                (p?.a?.id === b?.id && p?.b?.id === a?.id)
              );
              if (!alreadyIn) conflictPairs?.push({ a, b });
            }
          }
        }
        if (conflictPairs?.length > 0) {
          detectedConflicts?.push({
            resourceId,
            resource: assignments?.[0]?.resource,
            assignments,
            conflictPairs
          });
        }
      });

      setConflicts(detectedConflicts);
    } catch (err) {
      console.error('Error loading conflicts:', err?.message);
      setError(err?.message || 'Failed to load resource conflicts.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAndDetectConflicts();
  }, [loadAndDetectConflicts]);

  const fmt = (d) => d ? new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

  const { start: rangeStart, end: rangeEnd } = getDateRange();

  // Filter conflicts by date range and search/category
  const filteredConflicts = conflicts?.filter(conflict => {
    // Category filter
    if (categoryFilter !== 'all' && conflict?.resource?.category !== categoryFilter) return false;

    // Search filter
    if (searchTerm) {
      const term = searchTerm?.toLowerCase();
      const nameMatch = conflict?.resource?.name?.toLowerCase()?.includes(term);
      const assignmentMatch = conflict?.assignments?.some(a =>
        a?.projectName?.toLowerCase()?.includes(term) ||
        a?.phaseName?.toLowerCase()?.includes(term)
      );
      if (!nameMatch && !assignmentMatch) return false;
    }

    // Date range filter: at least one conflicting pair must overlap with the selected range
    if (rangeStart && rangeEnd) {
      const hasOverlapInRange = conflict?.conflictPairs?.some(({ a, b }) => {
        const overlapStart = new Date(Math.max(new Date(a?.startDate), new Date(b?.startDate)));
        const overlapEnd = new Date(Math.min(new Date(a?.endDate), new Date(b?.endDate)));
        return overlapStart <= rangeEnd && overlapEnd >= rangeStart;
      });
      if (!hasOverlapInRange) return false;
    }

    return true;
  });

  const handleDoubleClickAssignment = (assignment, resource) => {
    setEditingAssignment(assignment);
    setEditingResource(resource);
  };

  const handleSaveAssignment = async (assignment, updates) => {
    if (assignment?.source === 'direct') {
      const { error } = await resourceService?.updateAssignment(assignment?.rawId, {
        startDate: updates?.startDate,
        endDate: updates?.endDate
      });
      if (error) throw error;
    } else if (assignment?.source === 'deliverable') {
      // Update deliverable_resource_assignments
      const { error } = await supabase
        ?.from('deliverable_resource_assignments')
        ?.update({ start_date: updates?.startDate, end_date: updates?.endDate })
        ?.eq('id', assignment?.rawId);
      if (error) throw error;
    }
    await loadAndDetectConflicts();
  };

  const periodOptions = [
    { value: '7', label: 'Next 7 days' },
    { value: '14', label: 'Next 14 days' },
    { value: '30', label: 'Next 30 days' },
    { value: '60', label: 'Next 60 days' },
    { value: '90', label: 'Next 90 days' },
    { value: 'custom', label: 'Custom range' }
  ];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-4">
        <div className="flex flex-wrap gap-3 items-end">
          {/* Search */}
          <div className="flex-1 min-w-48">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Search</label>
            <div className="relative">
              <Icon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <input
                type="text"
                value={searchTerm}
                onChange={e => setSearchTerm(e?.target?.value)}
                placeholder="Search resource or project..."
                className="w-full pl-9 pr-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>
          </div>

          {/* Category */}
          <div className="min-w-40">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Category</label>
            <select
              value={categoryFilter}
              onChange={e => setCategoryFilter(e?.target?.value)}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {categories?.map(c => (
                <option key={c?.id} value={c?.id}>{c?.name}</option>
              ))}
            </select>
          </div>

          {/* Time period */}
          <div className="min-w-40">
            <label className="block text-xs font-medium text-muted-foreground mb-1">Time Period</label>
            <select
              value={periodPreset}
              onChange={e => setPeriodPreset(e?.target?.value)}
              className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
            >
              {periodOptions?.map(o => (
                <option key={o?.value} value={o?.value}>{o?.label}</option>
              ))}
            </select>
          </div>

          {/* Custom date range */}
          {periodPreset === 'custom' && (
            <>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">From</label>
                <input
                  type="date"
                  value={customStart}
                  onChange={e => setCustomStart(e?.target?.value)}
                  className="px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-muted-foreground mb-1">To</label>
                <input
                  type="date"
                  value={customEnd}
                  onChange={e => setCustomEnd(e?.target?.value)}
                  className="px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                />
              </div>
            </>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={loadAndDetectConflicts}
            iconName="RefreshCw"
            iconPosition="left"
          >
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary */}
      {!loading && (
        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2 px-4 py-2 bg-red-50 border border-red-200 rounded-lg">
            <Icon name="AlertTriangle" size={16} className="text-red-600" />
            <span className="text-sm font-semibold text-red-800">{filteredConflicts?.length}</span>
            <span className="text-sm text-red-700">resource{filteredConflicts?.length !== 1 ? 's' : ''} with conflicts</span>
          </div>
          {rangeStart && rangeEnd && (
            <p className="text-sm text-muted-foreground">
              Showing conflicts overlapping {fmt(rangeStart)} – {fmt(rangeEnd)}
            </p>
          )}
          <p className="text-xs text-muted-foreground ml-auto">
            Double-click an assignment to edit its dates
          </p>
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
            <p className="text-sm text-muted-foreground">Scanning for resource conflicts...</p>
          </div>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <Icon name="AlertCircle" size={40} className="text-red-500 mx-auto mb-3" />
          <p className="text-red-700 text-sm">{error}</p>
          <Button variant="outline" size="sm" onClick={loadAndDetectConflicts} className="mt-3">Retry</Button>
        </div>
      ) : filteredConflicts?.length === 0 ? (
        <div className="bg-card rounded-xl border border-border p-12 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle2" size={32} className="text-green-600" />
          </div>
          <h3 className="text-lg font-semibold text-foreground mb-2">No Conflicts Found</h3>
          <p className="text-sm text-muted-foreground">
            No resource conflicts detected for the selected time period and filters.
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredConflicts?.map((conflict) => (
            <div key={conflict?.resourceId} className="bg-card rounded-xl border border-red-200 overflow-hidden">
              {/* Resource header */}
              <div className="flex items-center gap-3 px-5 py-4 bg-red-50 border-b border-red-200">
                <div className="w-10 h-10 rounded-full overflow-hidden bg-muted flex-shrink-0">
                  <div className="w-full h-full bg-red-200 flex items-center justify-center">
                    <span className="text-red-700 font-semibold text-sm">
                      {conflict?.resource?.name?.charAt(0)?.toUpperCase() || '?'}
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{conflict?.resource?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{conflict?.resource?.category}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-red-100 text-red-700 text-xs font-medium rounded-full">
                    <Icon name="AlertTriangle" size={12} />
                    {conflict?.conflictPairs?.length} conflict{conflict?.conflictPairs?.length !== 1 ? 's' : ''}
                  </span>
                </div>
              </div>

              {/* Conflict pairs */}
              <div className="divide-y divide-border">
                {conflict?.conflictPairs?.map(({ a, b }, pairIdx) => {
                  const overlapStart = new Date(Math.max(new Date(a?.startDate), new Date(b?.startDate)));
                  const overlapEnd = new Date(Math.min(new Date(a?.endDate), new Date(b?.endDate)));
                  return (
                    <div key={pairIdx} className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <Icon name="GitMerge" size={14} className="text-red-500" />
                        <span className="text-xs font-medium text-red-700">
                          Overlap: {fmt(overlapStart)} – {fmt(overlapEnd)}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {[a, b]?.map((assignment) => (
                          <div
                            key={assignment?.id}
                            onDoubleClick={() => handleDoubleClickAssignment(assignment, conflict?.resource)}
                            title="Double-click to edit"
                            className="group flex items-start gap-3 p-3 bg-muted/40 hover:bg-accent/5 border border-border hover:border-accent/40 rounded-lg cursor-pointer transition-all select-none"
                          >
                            <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <Icon name="Calendar" size={14} className="text-accent" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-foreground truncate">
                                {assignment?.projectName || assignment?.phaseName || 'Assignment'}
                              </p>
                              {assignment?.phaseName && assignment?.projectName !== assignment?.phaseName && (
                                <p className="text-xs text-muted-foreground truncate">{assignment?.phaseName}</p>
                              )}
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {fmt(assignment?.startDate)} – {fmt(assignment?.endDate)}
                              </p>
                              <span className="inline-block mt-1 text-xs px-1.5 py-0.5 bg-muted rounded text-muted-foreground capitalize">
                                {assignment?.source}
                              </span>
                            </div>
                            <Icon
                              name="Pencil"
                              size={14}
                              className="text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-1"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* All assignments for this resource */}
              <div className="px-5 py-3 bg-muted/30 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  <span className="font-medium">{conflict?.assignments?.length}</span> total assignment{conflict?.assignments?.length !== 1 ? 's' : ''} for this resource
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Edit Assignment Modal */}
      {editingAssignment && editingResource && (
        <EditAssignmentModal
          assignment={editingAssignment}
          resource={editingResource}
          allAssignments={allAssignments}
          onClose={() => { setEditingAssignment(null); setEditingResource(null); }}
          onSave={handleSaveAssignment}
        />
      )}
    </div>
  );
};

export default ResourceConflictTab;
